import {
  DEFAULT_DIFFICULTY,
  VICTORY_MAX_GRADE_PCT,
  VICTORY_MIN_GRADE_PCT,
  type PowerSample,
  type TrainerDevice,
  type TrainerInfo,
} from "./types";

/**
 * Scale the road grade for trainer resistance. The simulation always uses the
 * actual grade; this only changes what the rider feels.
 */
export function trainerGrade(
  actualGradePct: number,
  difficulty: number = DEFAULT_DIFFICULTY,
  minPct = VICTORY_MIN_GRADE_PCT,
  maxPct = VICTORY_MAX_GRADE_PCT,
): number {
  const scale = Number.isFinite(difficulty)
    ? Math.max(0, Math.min(1, difficulty))
    : DEFAULT_DIFFICULTY;
  const grade = Number.isFinite(actualGradePct) ? actualGradePct : 0;
  const low = Math.min(minPct, maxPct);
  const high = Math.max(minPct, maxPct);
  return Math.max(low, Math.min(high, grade * scale));
}

/**
 * A trainer that needs no hardware. Useful for development, the client's
 * "sim power" mode, and automated tests.
 */
export class MockTrainer implements TrainerDevice {
  private info: TrainerInfo;
  private listeners = new Set<(sample: PowerSample) => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private grade = 0;
  private targetPower: number | null = null;
  private resistance = 0;

  constructor(
    private watts = 150,
    private cadence = 85,
    name = "Mock Trainer",
  ) {
    this.info = { id: "mock-trainer", name, tier: "A" };
  }

  async connect(): Promise<TrainerInfo> {
    this.start();
    return this.info;
  }

  async disconnect(): Promise<void> {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  onPower(callback: (sample: PowerSample) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  async setGrade(gradePct: number): Promise<void> {
    this.grade = gradePct;
  }

  async setTargetPower(watts: number): Promise<void> {
    this.targetPower = watts;
  }

  async setResistance(level: number): Promise<void> {
    this.resistance = level;
  }

  async calibrate(): Promise<void> {
    // No-op on a mock.
  }

  getInfo(): TrainerInfo | null {
    return this.info;
  }

  getLastCommand(): { grade: number; targetPower: number | null; resistance: number } {
    return { grade: this.grade, targetPower: this.targetPower, resistance: this.resistance };
  }

  setPower(watts: number): void {
    this.watts = watts;
  }

  setCadence(cadenceRpm: number): void {
    this.cadence = cadenceRpm;
  }

  private start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      const sample: PowerSample = {
        t: Date.now(),
        watts: this.watts,
        cadence: this.cadence,
        speed: 0,
      };
      for (const listener of [...this.listeners]) {
        try {
          listener(sample);
        } catch {
          // A listener error must not stop the mock power stream.
        }
      }
    }, 500);
  }
}
